import { useId, type ReactNode } from 'react';
import { Icon } from '../../design-system/icons';
import { InfoTip } from '../editor/StudioPanels';
import type { ScopeVariable } from '../editor/condition-model';
import type { PropertyDescriptor } from '../editor/schema-properties';
import type { PropertyGroup } from '../editor/editor-types';
import { text } from '../../app/i18n';
import type { Template } from './layout-model';

/**
 * Petits contrôles de l'inspecteur du profil « formulaire » (maquette FormulaireCanevas) : choix segmentés,
 * interrupteurs, listes, texte composé. Tout se lit en mots ; aucune valeur technique n'est montrée.
 */

export function Row({ label, help, children }: { label?: string; help?: string; children: ReactNode }) {
  return (
    <div className="ay-props__row">
      {label !== undefined && (
        <span className="ay-field__label ay-props__label">
          {label}
          {help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}
        </span>
      )}
      {children}
    </div>
  );
}

/** Section repliable de l'inspecteur : un en-tête clair, ouverte par défaut, refermée d'un clic. */
export function Section({ title, children, open = true }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details className="ay-studio-inspector__section" open={open}>
      <summary className="ay-studio-inspector__summary">
        <span>{title}</span>
        <Icon name="chevron-down" size={14} />
      </summary>
      <div className="ay-studio-inspector__section-body">{children}</div>
    </details>
  );
}

/** État vide ou constat utile au réglage : visible, discret. Une explication générale passe par `Hint`. */
export function Note({ children }: { children: ReactNode }) {
  return <p className="ay-studio-empty">{children}</p>;
}

/** Explication qui n'a pas besoin d'être lue à chaque fois : une ligne « En savoir plus » qui s'ouvre au clic. */
export function Hint({ label = 'En savoir plus', children }: { label?: string; children: ReactNode }) {
  return (
    <details className="ay-studio-hintline">
      <summary>
        <Icon name="info" size={14} />
        <span>{label}</span>
      </summary>
      <p>{children}</p>
    </details>
  );
}

/** Choix segmenté (un seul parmi peu) : un groupe de boutons radio. */
export function Seg<T extends string | number>({ label, options, value, onChange }: { label: string; options: ReadonlyArray<{ value: T; label: string }>; value: T | undefined; onChange: (value: T) => void }) {
  return (
    <Row label={label}>
      <span className="ay-studio__seg ay-studio-inspector__seg" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button key={String(option.value)} type="button" role="radio" aria-checked={value === option.value} onClick={() => onChange(option.value)}>{option.label}</button>
        ))}
      </span>
    </Row>
  );
}

export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  const id = useId();
  return (
    <div className="ay-props__row ay-props__row--inline">
      <input id={id} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <label htmlFor={id} className="ay-field__label">{label}</label>
    </div>
  );
}

export function Pick({ label, value, options, empty, onChange }: { label: string; value: string | undefined; options: ReadonlyArray<{ value: string; label: string }>; empty?: string; onChange: (value: string | undefined) => void }) {
  const id = useId();
  return (
    <div className="ay-props__row">
      <label htmlFor={id} className="ay-field__label">{label}</label>
      <select id={id} value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}>
        {empty !== undefined && <option value="">{empty}</option>}
        {value !== undefined && !options.some((option) => option.value === value) && <option value={value}>Choix qui n’existe plus</option>}
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

export function NumberInput({ label, value, min, max, onChange }: { label: string; value: number | undefined; min?: number; max?: number; onChange: (value: number | undefined) => void }) {
  const id = useId();
  return (
    <div className="ay-props__row">
      <label htmlFor={id} className="ay-field__label">{label}</label>
      <input id={id} type="number" inputMode="numeric" min={min} max={max} value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? undefined : Number(event.target.value))} />
    </div>
  );
}

export function Refusal({ message }: { message: string | null }) {
  return message === null ? null : <p className="ay-studio__refusal" role="alert">{message}</p>;
}

// ---- Texte composé (confirmation, succès) : des morceaux de texte et des valeurs choisies dans une liste.

type Part = { kind: 'text'; value: string } | { kind: 'value'; ref: string };

const REF = /^\$(aggregates|answers|result|context|form)(\.[A-Za-z0-9_]+)+$/;

/** Morceaux d'un texte composé ; `null` pour une expression qui n'est pas une simple suite de morceaux. */
export function partsOf(value: unknown): Part[] | null {
  if (value === undefined || value === null) return [];
  const one = (item: unknown): Part | null => {
    if (typeof item === 'string') return REF.test(item) ? { kind: 'value', ref: item } : item.startsWith('$') ? null : { kind: 'text', value: item };
    if (item !== null && typeof item === 'object' && Object.keys(item).length === 1 && typeof (item as { $literal?: unknown }).$literal === 'string') return { kind: 'text', value: (item as { $literal: string }).$literal };
    return null;
  };
  if (typeof value === 'string' || (value !== null && typeof value === 'object' && '$literal' in value)) {
    const single = one(value);
    return single === null ? null : [single];
  }
  const items = (value as { $concat?: unknown }).$concat;
  if (!Array.isArray(items)) return null;
  const parts = items.map(one);
  return parts.every((part): part is Part => part !== null) ? parts : null;
}

/** Expression d'un texte composé : texte seul, ou `$concat` ; un texte qui commence par `$` passe par `$literal`. */
export function composeOf(parts: readonly Part[]): unknown {
  const items = parts.filter((part) => part.kind === 'value' || part.value !== '').map((part) => (part.kind === 'value' ? part.ref : part.value.startsWith('$') ? { $literal: part.value } : part.value));
  if (items.length === 0) return undefined;
  if (items.length === 1 && parts.every((part) => part.kind === 'text')) return items[0];
  return { $concat: items };
}

export function TextComposer({ label, value, sources, onChange }: { label: string; value: unknown; sources: ReadonlyArray<{ ref: string; label: string }>; onChange: (value: unknown) => void }) {
  const parts = partsOf(value);
  if (parts === null) return <Row label={label}><span className="ay-text">Texte calculé par une formule (il se reprend en mode développeur).</span></Row>;
  const write = (next: Part[]): void => onChange(composeOf(next));
  return (
    <fieldset className="ay-props__group ay-studio-composer">
      <legend className="ay-field__label">{label}</legend>
      <p className="ay-studio__muted" aria-live="polite">
        Aperçu : {parts.length === 0 ? '—' : parts.map((part) => (part.kind === 'text' ? part.value : `[${sources.find((source) => source.ref === part.ref)?.label ?? 'valeur'}]`)).join('')}
      </p>
      {parts.map((part, index) => (
        <span key={index} className="ay-props__list-row">
          {part.kind === 'text' ? (
            <input aria-label={`${label} : texte ${index + 1}`} type="text" value={part.value} onChange={(event) => write(parts.map((candidate, position) => (position === index ? { kind: 'text', value: event.target.value } : candidate)))} />
          ) : (
            <select aria-label={`${label} : valeur ${index + 1}`} value={part.ref} onChange={(event) => write(parts.map((candidate, position) => (position === index ? { kind: 'value', ref: event.target.value } : candidate)))}>
              {!sources.some((source) => source.ref === part.ref) && <option value={part.ref}>Valeur qui n’existe plus</option>}
              {sources.map((source) => <option key={source.ref} value={source.ref}>{source.label}</option>)}
            </select>
          )}
          <button type="button" className="ay-editor__tool" aria-label={`Retirer le morceau ${index + 1}`} onClick={() => write(parts.filter((_, position) => position !== index))}><Icon name="close" size={14} /></button>
        </span>
      ))}
      <span className="ay-props__list-row">
        <button type="button" className="ay-button ay-button--secondary" onClick={() => write([...parts, { kind: 'text', value: '' }])}>+ Texte</button>
        <button type="button" className="ay-button ay-button--secondary" disabled={sources.length === 0} onClick={() => write([...parts, { kind: 'value', ref: sources[0]!.ref }])}>+ Valeur</button>
      </span>
    </fieldset>
  );
}

// ---- Panneau de propriétés filtré par onglet (propriétés générées du contrat).

/** Garde les propriétés d'un groupe : par clé de premier niveau, et pour un objet, ses sous-clés choisies. */
export function pickProperties(group: PropertyGroup<Template>, spec: Readonly<Record<string, readonly string[] | true>>): PropertyGroup<Template> {
  const keep = (descriptor: PropertyDescriptor): PropertyDescriptor | null => {
    const rule = spec[descriptor.key];
    if (rule === undefined) return null;
    if (rule === true || descriptor.children === undefined) return descriptor;
    const children = descriptor.children.filter((child) => rule.includes(child.key));
    return children.length === 0 ? null : { ...descriptor, children };
  };
  return { ...group, descriptors: group.descriptors.map(keep).filter((descriptor): descriptor is PropertyDescriptor => descriptor !== null), reserved: [] };
}

/** Sources d'un texte composé ou d'une liaison : totaux, réponses de la fiche, données de la tâche. */
export function valueSources(template: Template, roots: { aggregates?: boolean; answers?: boolean; context?: boolean; result?: boolean } = { aggregates: true, answers: true }): Array<{ ref: string; label: string }> {
  const out: Array<{ ref: string; label: string }> = [];
  if (roots.aggregates === true) for (const aggregate of template.layout?.aggregates ?? []) out.push({ ref: `$aggregates.${aggregate.aggregate_id}`, label: `Total : ${text(aggregate.label, 'fr', 'sans nom')}` });
  if (roots.answers === true) {
    for (const section of template.sections) {
      if (section.repeat !== undefined || section.series !== undefined) continue;
      for (const entry of section.fields) out.push({ ref: `$answers.${entry.field.field_id}`, label: `Réponse : ${text(entry.field.label, 'fr', 'champ sans nom')}` });
    }
  }
  if (roots.context === true) out.push({ ref: '$context.task.sheet_date', label: 'Date de la fiche' }, { ref: '$context.operator.display_name', label: 'Opérateur' });
  if (roots.result === true) out.push({ ref: '$result.document_number', label: 'Numéro du document créé' });
  return out;
}

/** Variables d'une condition de page ou de bloc : les réponses de la fiche. */
export function answerScope(template: Template): ScopeVariable[] {
  return template.sections
    .filter((section) => section.repeat === undefined && section.series === undefined)
    .flatMap((section) =>
      section.fields.map((entry): ScopeVariable => {
        const type = entry.field.type;
        const values = entry.field.validation?.enum_values ?? [];
        const labels = (entry.field.validation?.enum_labels ?? {}) as Record<string, Record<string, string>>;
        return {
          ref: `$answers.${entry.field.field_id}`,
          label: text(entry.field.label, 'fr', 'champ sans nom'),
          type: type === 'enumeration' ? 'enum' : type === 'boolean' ? 'boolean' : ['number', 'decimal', 'quantity', 'money'].includes(type) ? 'number' : type === 'date' ? 'date' : type === 'string' ? 'string' : 'other',
          ...(type === 'enumeration' ? { options: values.map((value) => ({ value, label: text(labels[value], 'fr', value) })) } : {}),
        };
      }),
    );
}
